import React, { useState } from "react";
import { format } from "date-fns";
import Avatar from "../common/Avatar";
import toast from "react-hot-toast";

const statusColors = {
  scheduled: "bg-emerald-500",
  started: "bg-indigo-500",
  ended: "bg-gray-400",
  cancelled: "bg-red-500",
};

const MeetingCard = ({ meeting, isHost, onStartJoin, onEdit, onCancel }) => {
  const {
    topic,
    schedule_date,
    duration,
    status,
    schedule_by,
    schedule_for,
    join_url,
    timezone,
  } = meeting;

  const [copied, setCopied] = useState(false);

  const formattedDate = format(new Date(schedule_date), "d MMM yyyy");
  const startTime = format(new Date(schedule_date), "HH:mm");

  // Calculate end time
  const endDate = new Date(
    new Date(schedule_date).getTime() + (duration || 60) * 60000
  );
  const endTime = format(endDate, "HH:mm");

  const statusColor = statusColors[status] || "bg-gray-400";

  // Extract timezone abbreviation (e.g., "GMT+7" from "Asia/Calcutta")
  const getTimezoneAbbr = (tz) => {
    if (!tz) return "";
    try {
      const date = new Date();
      const offset = new Intl.DateTimeFormat("en", {
        timeZone: tz,
        timeZoneName: "short",
      })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value;
      return offset || "";
    } catch {
      return "";
    }
  };

  const timezoneDisplay = getTimezoneAbbr(timezone);

  const handleCopyLink = () => {
    if (join_url) {
      navigator.clipboard.writeText(join_url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      style={{
        boxShadow: "0px 0px 0px 0.5px #00000008, 0px 5px 22px 0px #0000000A",
      }}
      className="bg-white rounded-xl border border-gray-200 max-w-2xl mx-auto"
    >
      {/* Header */}
      <div className="p-6">
        <p className="text-xs font-medium text-[#1A212F99] mb-2">
          New Zoom meet
        </p>
        <h2 className="text-xl  font-medium text-[#1A212F] mb-4">{topic}</h2>

        {/* Date and Time */}
        <div className="flex items-center gap-6 text-gray-700 justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#1A212F] font-medium bg-[#F9F9F9] p-2 rounded-lg">
              {formattedDate}
            </span>
            <span className="text-sm text-[#1A212F] font-medium bg-[#F9F9F9] p-2 rounded-lg">
              {startTime}
            </span>
            <span>-</span>
            <span className="text-sm text-[#1A212F] font-medium bg-[#F9F9F9] p-2 rounded-lg">
              {endTime}
            </span>
          </div>
          {timezoneDisplay && (
            <div className="flex items-center gap-1">
              <span className="text-[#1A212F99] text-sm font-medium">
                Timezone
              </span>
              <span className="text-[#006BF3] text-sm font-medium">
                {timezoneDisplay}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Link to share section */}
      <div
        style={{
          background:
            "linear-gradient(184.47deg, #FFFFFF 3.42%, #F2F2F2 104.93%)",
        }}
        className="p-4 w-[90%] mx-auto border-4 border-[#F2F2F2] rounded-3xl"
      >
        <div className="mb-4">
          <h3 className="text-base font-medium text-[#1A212F] mb-1">
            Link to share
          </h3>
          <p className="text-xs text-[#1A212F99] font-medium">
            Anyone with the link can join the meeting
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={join_url || ""}
            readOnly
            disabled={true}
            className="flex-1 px-4 py-2.5 bg-white border border-[#D6DBEA] rounded-lg text-sm text-[#1A212F99] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleCopyLink}
            style={{
              background:
                "linear-gradient(90deg, rgba(126, 139, 255, 0.2) 0%, rgba(34, 131, 255, 0.2) 100%)",
            }}
            className="px-6 py-2.5  text-black text-sm font-medium rounded-lg hover:bg-blue-200 transition-colors"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      {/* Guests section */}
      <div className="p-4 mt-4 w-[90%] mx-auto border border-[#D6DBEA] rounded-3xl">
        <div className="flex items-center gap-3 mb-4 border-b border-[#D6DBEA] pb-4">
          <svg
            width="16"
            height="14"
            viewBox="0 0 16 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.2666 12.5996V11.2663C11.2666 10.559 10.9857 9.88076 10.4856 9.38066C9.9855 8.88056 9.30722 8.59961 8.59998 8.59961H3.26664C2.5594 8.59961 1.88112 8.88056 1.38102 9.38066C0.880927 9.88076 0.599976 10.559 0.599976 11.2663V12.5996M15.2666 12.5996V11.2663C15.2662 10.6754 15.0695 10.1015 14.7076 9.63449C14.3456 9.16751 13.8387 8.83399 13.2666 8.68628M10.6 0.686276C11.1736 0.833143 11.682 1.16674 12.0451 1.63448C12.4081 2.10222 12.6052 2.6775 12.6052 3.26961C12.6052 3.86172 12.4081 4.437 12.0451 4.90474C11.682 5.37248 11.1736 5.70608 10.6 5.85294M8.59998 3.26628C8.59998 4.73904 7.40607 5.93294 5.93331 5.93294C4.46055 5.93294 3.26664 4.73904 3.26664 3.26628C3.26664 1.79352 4.46055 0.599609 5.93331 0.599609C7.40607 0.599609 8.59998 1.79352 8.59998 3.26628Z"
              stroke="#43526E"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <h3 className="text-base font-medium text-[#1A212F]">Guests</h3>
          <span className="text-sm text-[#1A212F99] font-medium">2 People</span>
        </div>

        <div className="space-y-3">
          {/* Host */}
          <div className="flex items-center gap-3">
            <Avatar
              src={schedule_by?.avatar}
              alt={schedule_by?.name || "Host"}
              size={40}
              fallbackText={schedule_by?.name || schedule_by?.email || "H"}
            />
            <div className="flex-1">
              <p className="font-medium text-[#1A212F] text-sm">
                {schedule_by?.name || "Host"}
              </p>
              <p className="text-xs text-[#1A212F99]">{schedule_by?.email}</p>
            </div>
          </div>

          {/* Guest */}
          <div className="flex items-center gap-3">
            <Avatar
              src={schedule_for?.avatar}
              alt={schedule_for?.name || "Guest"}
              size={40}
              fallbackText={schedule_for?.name || schedule_for?.email || "G"}
            />
            <div className="flex-1">
              <p className="font-medium text-[#1A212F] text-sm">
                {schedule_for?.name || "Guest"}
              </p>
              <p className="text-xs text-[#1A212F99]">{schedule_for?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="w-[90%] mx-auto py-6 flex items-center gap-3">
        {status === "scheduled" && (
          <button
            type="button"
            onClick={onStartJoin}
            className="px-6 py-2.5 bg-[#2283FF] text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            {isHost ? "Join meeting" : "Join meeting"}
          </button>
        )}

        {isHost && status === "scheduled" && (
          <button
            type="button"
            onClick={onEdit}
            className="px-6 py-2.5 bg-[#F4F4F4] text-black text-sm font-medium rounded-lg"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
};

export default MeetingCard;
